import React from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getConversion } from '../../utils/statisticsHelpers';

interface ConversionWidgetProps {
  data: StatisticsSummaryView | null;
}

/**
 * ТЗ п.3: Conversion area chart with gradient fill.
 * Layout: metrics column on the left, chart on the right.
 * Per-day conversion = salesByDay[date].orderCount / visitorsByDay[date].visitorCount * 100
 */
export const ConversionWidget: React.FC<ConversionWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const conversion = getConversion(data);
  const sessions = conversion?.visitorCount ?? 0;
  const orders = conversion?.orderCount ?? 0;
  const rate = conversion?.conversionPercent ?? 0;

  const trendData = React.useMemo(() => {
    const salesByDay: any[] = data?.salesByDay ?? [];
    const visitorsByDay: any[] = data?.visitorsByDay ?? [];

    if (visitorsByDay.length === 0) return [];

    const visitorMap = new Map<string, number>();
    visitorsByDay.forEach((v: any) => {
      visitorMap.set(v.date, v.visitorCount ?? 0);
    });

    const orderMap = new Map<string, number>();
    salesByDay.forEach((s: any) => {
      orderMap.set(s.date, s.orderCount ?? 0);
    });

    return visitorsByDay
      .map((v: any) => {
        const visitors = v.visitorCount ?? 0;
        const dayOrders = orderMap.get(v.date) ?? 0;
        return {
          name: v.date,
          rate: visitors > 0 ? (dayOrders / visitors) * 100 : 0,
          orders: dayOrders,
          visitors,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_conversion')}</h3>
      </div>
      <div className="conversion-body">
        <div className="conversion-metrics">
          <div className="conversion-metric">
            <span className="conversion-metric-label">{t('dashboard_stats_total_sessions')}:</span>
            <span className="conversion-metric-value">{sessions.toLocaleString()}</span>
          </div>
          <div className="conversion-metric">
            <span className="conversion-metric-label">{t('dashboard_stats_total_orders')}:</span>
            <span className="conversion-metric-value">{orders}</span>
          </div>
          <div className="conversion-metric">
            <span className="conversion-metric-label">{t('dashboard_stats_avg_conversion_rate')}:</span>
            <span className="conversion-metric-value">{rate.toFixed(1)}%</span>
          </div>
        </div>
        {trendData.length > 0 && (
          <div className="conversion-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="conversionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" unit="%" />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'rate') return [`${Number(value).toFixed(3)}%`, t('dashboard_stats_conversion')];
                    return [value, name];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke="#2563EB"
                  strokeWidth={2}
                  fill="url(#conversionGradient)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
